"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin } from "lucide-react"   
import { format, addWeeks, subWeeks, parseISO, isSameDay, set } from "date-fns"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { ScrollArea } from "../../components/ui/scroll-area"
import { cs } from "date-fns/locale"
import { 
  motion,
  LazyMotion, 
  domAnimation,
} from "framer-motion"

import { Button } from "../../components/ui/button"
import { TimeSlotModal } from "./time-slot-modal"
import type { PrijmaciRizeni } from "./types"

import HudebniImg from "../../data/obory/icons/wHudba.png"
import TanecniImg from "../../data/obory/icons/wTanec.png"
import VytvarnyImg from "../../data/obory/icons/wVytvarnypng.png"
import Alert from "../../components/Alert"

const obory = [
  {
    id: "hudebni",
    name: "Hudební",
    color: "from-[#4f7971] to-[#4f7971]",
    hoverColor: "hover:bg-purple-100",
    icon: HudebniImg,
    description: "Dítě si připraví a zazpívá jednoduchou (lidovou) píseň. např. Pec nám spadla, Kočka leze dírou, Holka modrooká, Skákal pes, Běží liška k Táboru či jinou."
  },
  {
    id: "vytvarny",
    name: "Výtvarný",
    color: "from-[#d4b682] to-[#d4b682]",
    hoverColor: "hover:bg-orange-100",
    icon: VytvarnyImg,
    description: "Dítě si může přinést vlastní výkresy s sebou. Všechny výtvarné pomůcky budou k dispozici na místě."
  },
  {
    id: "tanecni",
    name: "Taneční",
    color: "from-[#96522B] to-[#96522B]",
    hoverColor: "hover:bg-blue-100",
    icon: TanecniImg,
    description: "Sportovní oblečení s sebou. Dítě si připraví krátkou taneční či gymnastickou variaci."
  },
]

export default function PrijmaciRizeniPage() {
  const [selectedObor, setSelectedObor] = React.useState<string>("")
  const [currentWeek, setCurrentWeek] = React.useState(new Date("2025-02-17"))
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isCityModalOpen, setIsCityModalOpen] = React.useState(false)
  const [selectedDay, setSelectedDay] = React.useState<string | null>(null)
  const [selectedCity, setSelectedCity] = React.useState<string | null>(null)
  const [prijmaciRizeni, setPrijmaciRizeni] = React.useState<PrijmaciRizeni | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [showAlert, setShowAlert] = React.useState(false)

  const fetchPrijmaciRizeni = React.useCallback(async (obor: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(`https://preview.zushm.cz/api/prihlasky/prijmacirizeni?obor=${obor}`)
      if (!response.ok) {
        throw new Error("Failed to fetch data")
      }
      const data = await response.json()
      setPrijmaciRizeni(data)

      if (data.dny && data.dny.length > 0) {
        const sortedDays = [...data.dny].sort((a, b) =>
          new Date(a.datum).getTime() - new Date(b.datum).getTime()
        )
        const firstAvailableDay = parseISO(sortedDays[0].datum)
        const monday = new Date(firstAvailableDay)
        monday.setDate(monday.getDate() - monday.getDay() + 1)
        setCurrentWeek(monday)
      }
    } catch (err) {
      setError("Přijímací řízení momentálně neprobíhá. Prosím, zkuste to znovu později.")
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (selectedObor) {
      fetchPrijmaciRizeni(selectedObor)
    }
  }, [selectedObor, fetchPrijmaciRizeni])

  const handlePreviousWeek = () => {
    setCurrentWeek((prev) => subWeeks(prev, 1))
  }

  const AlertFunc = () => {
    setShowAlert(true)
    setTimeout(() => {
      setShowAlert(false)
    }, 5000)
  }

  const handleNextWeek = () => {
    setCurrentWeek((prev) => addWeeks(prev, 1))
  }

  const handleDayClick = (date: string) => {
    setSelectedDay(date)
    setIsCityModalOpen(true)
  }

  const handleCitySelect = (city: string) => {
    setSelectedCity(city)
    setIsCityModalOpen(false)
    setIsModalOpen(true)
  }

  const weekDays = Array.from({ length: 5 }).map((_, i) => {
    const date = addWeeks(currentWeek, 0)
    date.setDate(currentWeek.getDate() + i)
    return date
  })

  const selectedOborData = obory.find((o) => o.id === selectedObor)

  const hasTimes = (date: Date) => {
    return prijmaciRizeni?.dny.some((den) => isSameDay(parseISO(den.datum), date) && den.casy.length > 0)
  }

  const availableCities = React.useMemo(() => {
    if (!prijmaciRizeni || !selectedDay) return []
    return prijmaciRizeni.dny
      .filter((den) => isSameDay(parseISO(den.datum), parseISO(selectedDay)))
      .map((den) => den.mesto)
  }, [prijmaciRizeni, selectedDay])

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6 mt-56 mb-24">
      {!selectedObor ? (
        <div className="space-y-6">
          <h1 className="text-center text-2xl sm:text-3xl font-bold">Vyberte obor pro přijímací řízení</h1>
          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {obory.map((obor) => (
              <motion.button
                key={obor.id}
                onClick={() => setSelectedObor(obor.id)}
                className={`group relative overflow-hidden rounded-xl bg-gradient-to-r ${obor.color} p-6 sm:p-8 text-white shadow-lg transition-all hover:scale-105`}
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="relative z-10 flex flex-col items-center gap-4">
                  <img 
                    src={obor.icon} 
                    alt={obor.name} 
                    className="w-16 h-16 object-contain"
                  />
                  <h2 className="text-xl sm:text-2xl font-bold">{obor.name}</h2>
                  <p className="text-xs sm:text-sm text-white/90">Klikněte pro výběr termínu</p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </motion.button>
            ))}
          </div>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Button variant="ghost" onClick={() => setSelectedObor("")} className="flex items-center gap-2">
              ← Zpět na výběr oboru
            </Button>
            <h2 className="text-xl sm:text-2xl font-bold">{selectedOborData?.name} obor</h2>
          </div>

          {isLoading ? (
            <div className="text-center">Načítání...</div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : prijmaciRizeni ? (
            <div className="rounded-xl bg-gradient-to-b from-blue-50 to-blue-100 p-4 sm:p-6 shadow-lg">
              <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <Button variant="outline" onClick={handlePreviousWeek} className="flex items-center gap-2">
                  <ChevronLeft className="h-4 w-4" />
                  Předchozí týden
                </Button>
                <h2 className="text-lg sm:text-xl font-semibold text-center">
                  {format(weekDays[0], "d. MMMM", { locale: cs })} -{" "}
                  {format(weekDays[4], "d. MMMM yyyy", { locale: cs })}
                </h2>
                <Button variant="outline" onClick={handleNextWeek} className="flex items-center gap-2">
                  Příští týden
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {weekDays.map((date) => (
                  <div key={date.toISOString()} className="space-y-2">
                    <h3 className="text-center text-sm sm:text-base font-medium">
                      {format(date, "EEEE d. MMMM", { locale: cs })}
                    </h3>
                    <motion.button
                      onClick={() => handleDayClick(date.toISOString())}
                      className={`group relative h-24 sm:h-32 md:h-40 w-full overflow-hidden rounded-lg bg-white p-2 sm:p-4 shadow-sm transition-all hover:shadow-md ${selectedOborData?.hoverColor} ${
                        hasTimes(date) ? "border-2 border-green-500" : ""
                      }`}
                      whileHover={{ scale: 1.02 }}
                      disabled={!hasTimes(date)}
                    >
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 sm:gap-3 p-2 sm:p-4">
                        {hasTimes(date) ? (
                          <>
                            <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 transition-transform group-hover:scale-110" />
                            <div className="text-center">
                              <p className="text-xs sm:text-sm font-medium text-green-600">Dostupné termíny</p>
                              <p className="mt-1 text-xs text-gray-400 hidden sm:block">Klikněte pro výběr času</p>
                            </div>
                          </>
                        ) : (
                          <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-300" />
                        )}
                      </div>
                    </motion.button>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 sm:p-6 bg-gray-200 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold">Požadavky pro {selectedOborData?.name} obor</h3>
                <p className="text-sm sm:text-base text-gray-700">{selectedOborData?.description}</p>
              </div>
            </div>
          ) : null}
        </motion.div>
      )}

      <LazyMotion features={domAnimation}>
        {isCityModalOpen && (
          <Dialog open={isCityModalOpen} onOpenChange={setIsCityModalOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Vyberte město</DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-[60vh] px-1">
                <div className="space-y-2">
                  {availableCities.map((city) => (
                    <Button
                      key={city}
                      onClick={() => handleCitySelect(city)}
                      className="w-full justify-start text-left"
                      variant="ghost"
                    >
                      <MapPin className="mr-2 h-4 w-4" />
                      {city}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        )}
        {isModalOpen && selectedDay && prijmaciRizeni && selectedCity && (
          <TimeSlotModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            date={selectedDay}
            prijmaciRizeni={prijmaciRizeni}
            oborId={selectedObor}
            selectedCity={selectedCity}
            toaster={() => AlertFunc()}
          />
        )}

        {showAlert && (
          <Alert
          message="Přijímací řízení ještě nezačalo."
          variant="error"
          dismissible={false}
          />
        )}
      </LazyMotion>
    </div>
  )
}